interface GazetaDoPovoUser {
  id: string | number;
  hasBasic?: boolean;
  hasPremium?: boolean;
  hasDigital?: boolean;
  cpf?: string | null;
  displayName?: string | null;
  Emails?: Array<{ email: string }>;
}

interface GazetaDoPovoAccount {
  isLogged(): boolean;
  getSessionData(): { id: string | number };
  getUser(id: string | number): Promise<{ Response: { data: GazetaDoPovoUser } }>;
}

interface GazetaDoPovoSDK {
  Account: GazetaDoPovoAccount;
}

declare global {
  interface Window {
    GazetaDoPovo?: GazetaDoPovoSDK;
  }
}

export interface GZSession {
  is_subscriber: boolean;
  subscriber_id: string;
  subscriber_email: string;
  subscriber_cpf: string;
  subscriber_name: string;
}

const DEFAULT_TIMEOUT_MS = 4000;
const POLL_INTERVAL_MS = 100;

/**
 * Lê a sessão do usuário logado da Gazeta do Povo a partir do SDK
 * `window.GazetaDoPovo.Account` (carregado por `GazetaDoPovoSDK.js`).
 *
 * O SDK carrega de forma assíncrona, então esta função faz polling curto pela
 * presença do SDK até `timeoutMs` (default 4000ms). Assim que ele aparece,
 * decide: se não há login, resolve `null`; se há, busca o usuário e mapeia.
 *
 * **Nunca lança** — resolve `null` em qualquer cenário de ausência/erro/não-logado.
 */
export function getGZSession(
  options?: { timeoutMs?: number },
): Promise<GZSession | null> {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return new Promise<GZSession | null>((resolve) => {
    if (typeof window === "undefined") {
      resolve(null);
      return;
    }

    const deadline = Date.now() + timeoutMs;

    const tryResolve = (): void => {
      const account = window.GazetaDoPovo?.Account;

      if (!account) {
        if (Date.now() >= deadline) {
          resolve(null);
          return;
        }
        setTimeout(tryResolve, POLL_INTERVAL_MS);
        return;
      }

      try {
        if (!account.isLogged()) {
          resolve(null);
          return;
        }

        const session = account.getSessionData();
        account
          .getUser(session.id)
          .then((data) => {
            const user = data?.Response?.data;
            if (!user) {
              resolve(null);
              return;
            }
            resolve({
              is_subscriber:
                !!user.hasBasic || !!user.hasPremium || !!user.hasDigital,
              subscriber_id: String(user.id),
              subscriber_email: user.Emails?.[0]?.email ?? "",
              subscriber_cpf: user.cpf ?? "",
              subscriber_name: user.displayName ?? "",
            });
          })
          .catch(() => resolve(null));
      } catch {
        resolve(null);
      }
    };

    tryResolve();
  });
}
